import { kMedChronApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { throwIfEditingLeaseDenial } from '@/editing-leases/schemas'
import { readResponseJson, getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { ApiError } from '@/util/errors/api-error'
import { RemoveFileResponse, RemoveFileResponseSchema } from '../store/schemas'

/**
 * Synchronously removes a file from the chronology and regenerates it from the rest.
 * Throws ApiError(409) when an update run is active.
 * Typed editing-lease denials also throw ApiError(409) with their structured detail.
 */
export async function deleteAssetFile(assetId: string, userFileId: string): Promise<RemoveFileResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'DELETE',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMedChronApiBasePath}/asset/${assetId}/files/${userFileId}`
    const response = await fetch(apiUrl, requestOptions)

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
      throwIfEditingLeaseDenial(response.status, detail)
      Sentry.captureException(new Error(`Chronology removal failed: ${response.status}`), {
        tags: { transaction_id: sentry_transaction_id },
        extra: getResponseDebugContext(response, apiUrl),
      })
      throw new ApiError(response.status, 'Could not remove the file', detail)
    }
    const parsed = RemoveFileResponseSchema.safeParse(data)
    if (parseError || !parsed.success) {
      Sentry.captureException(new Error('Invalid chronology removal response'), {
        tags: { transaction_id: sentry_transaction_id },
        extra: { ...getResponseDebugContext(response, apiUrl), responseKeys: getTopLevelKeys(data) },
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid chronology removal response')
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.captureException(error, {
        tags: { transaction_id: sentry_transaction_id },
        extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
      })
    }
    throw error
  }
}
